import React from "react";
import dayjs from "dayjs";
import { FileTextOutlined } from "@ant-design/icons";
import { COMMON_DICTS, getMetadataLabel } from "@ifa/utils";
import styles from "./ProductView.module.css";

interface ProductViewProps {
  product: any;
}

export function ProductView({ product }: ProductViewProps) {
  // Custom InfoCard component
  const InfoCard = ({ title, children }: { title: string; children?: React.ReactNode }) => (
    <div className={styles["info-card"]}>
      <div className={styles["card-header"]}>
        <h3 className={styles["card-title"]}>{title}</h3>
      </div>
      {children && <div className={styles["card-body"]}>{children}</div>}
    </div>
  );

  // Custom InfoField component
  const InfoField = ({ label, value, span = 1 }: { label: string; value: React.ReactNode; span?: 1 | 2 }) => (
    <div className={`${styles["info-field"]} ${span === 2 ? styles["span-2"] : ""}`}>
      <div className={styles["field-label"]}>{label}</div>
      <div className={styles["field-value"]}>{value || "-"}</div>
    </div>
  );

  if (!product)
    return (
      <div className={styles["empty-state"]}>
        <FileTextOutlined style={{ fontSize: 64, color: "#d9d9d9", marginBottom: 16 }} />
        <div>暫無產品資料</div>
      </div>
    );

  // Build other settings fields dynamically based on conditions
  const getOtherSettingsFields = () => {
    const fields = [];

    fields.push({ label: "是否預繳", value: product.isPrepaid ? "是" : "否" });
    if (product.isPrepaid) {
      fields.push({
        label: "預繳額(含首期)",
        value: product.prepaidAmountInclFirst != null ? `${product.prepaidAmountInclFirst} ${product.currency || ""}` : "-",
      });
    }

    fields.push({ label: "是否保費融資", value: product.isPremiumFinanced ? "是" : "否" });
    fields.push({ label: "是否保單回溯", value: product.isPolicyBackdated ? "是" : "否" });

    if (product.isPolicyBackdated) {
      fields.push({
        label: "保單回溯日期",
        value: product.policyBackDate ? dayjs(product.policyBackDate).format("YYYY-MM-DD") : "-",
      });
    }

    fields.push({ label: "申請客戶特殊首年保費折扣", value: product.applySpecialFirstYearDiscount ? "是" : "否" });

    if (product.applySpecialFirstYearDiscount) {
      fields.push({
        label: "客戶特殊首年保費折扣比例",
        value: product.specialFirstYearDiscountRate != null ? `${product.specialFirstYearDiscountRate}%` : "-",
      });
      fields.push({
        label: "扣佣比例",
        value: product.commissionRate != null ? `${product.commissionRate}%` : "-",
      });
    }

    fields.push({ label: "是否參加遞增保障權益", value: product.isIncreasingProtectionIncluded ? "是" : "否" });
    fields.push({ label: "是否申請過DDA", value: product.isDdaSupported ? "是" : "否" });

    return fields;
  };

  return (
    <div className={styles["detail-content"]}>
      {/* Main Insurance / Basic Plan */}
      <InfoCard title="主險/基本計劃">
        <div className={styles["fields-grid"]}>
          <InfoField label="投保產品" value={<span style={{ fontWeight: 600 }}>{product.productName || "-"}</span>} />
          <InfoField label="保險公司" value={product.companyName || "-"} />
          <InfoField label="銷售地區" value="香港" />
          <InfoField label="險種" value={product.categoryName || "-"} />
        </div>
      </InfoCard>

      {/* Benefits & Payment */}
      <InfoCard title="保障及繳費">
        <div className={styles["fields-grid"]}>
          <InfoField label="幣種" value={getMetadataLabel(null, product.currency, COMMON_DICTS.currency)} />
          <InfoField label="每期保費" value={product.premiumPerPeriod != null ? `${product.premiumPerPeriod} ${product.currency || ""}` : "-"} />
          <InfoField label="保額" value={product.sumAssured != null ? `${product.sumAssured} ${product.currency || ""}` : "-"} />
          <InfoField label="保障期" value={getMetadataLabel("PERIOD", product.protectionPeriod, COMMON_DICTS.protectionPeriod) || "-"} />
          <InfoField label="供款年期" value={product.contributionYears || "-"} />
          <InfoField label="繳費頻率" value={getMetadataLabel(null, product.paymentFrequency, COMMON_DICTS.paymentFrequency)} />
          <InfoField label="首期折扣後保費" value={product.firstYearDiscountedPremium != null ? `${product.firstYearDiscountedPremium} ${product.currency || ""}` : "-"} />
          <InfoField label="首期繳費方法" value={getMetadataLabel(null, product.firstPaymentMethod, COMMON_DICTS.firstPaymentMethod)} />
          <InfoField label="續期繳費方法" value={getMetadataLabel(null, product.renewalPaymentMethod, COMMON_DICTS.firstPaymentMethod)} />
          <InfoField label="紅利分派方式" value={getMetadataLabel(null, product.dividendDistributionMethod, COMMON_DICTS.dividendDistributionMethod)} />
        </div>
      </InfoCard>

      {/* Additional Settings */}
      <InfoCard title="其他設定">
        <div className={styles["fields-grid"]}>
          {getOtherSettingsFields().map((field, index) => (
            <InfoField key={index} label={field.label} value={field.value} />
          ))}
        </div>
      </InfoCard>

      {/* Riders */}
      {product.riders && product.riders.length > 0 && (
        <InfoCard title="附加保障">
          <div className={styles["riders-container"]}>
            {product.riders.map((rider: any, rIdx: number) => (
              <div key={rIdx} className={styles["rider-item"]}>
                <div className={styles["rider-title"]}>附加保障 {rIdx + 1}</div>
                <div className={styles["fields-grid"]}>
                  <InfoField label="投保產品" value={rider.productName || "-"} />
                  <InfoField label="保險公司" value={rider.companyName || "-"} />
                  <InfoField label="銷售地區" value="香港" />
                  <InfoField label="險種" value={rider.categoryName || "-"} />
                  <InfoField label="幣種" value={getMetadataLabel(null, rider.currency, COMMON_DICTS.currency)} />
                  <InfoField label="每期保費" value={rider.premiumPerPeriod != null ? `${rider.premiumPerPeriod}` : "-"} />
                  <InfoField label="保額" value={rider.sumAssured != null ? `${rider.sumAssured}` : "-"} />
                  <InfoField label="保障期" value={getMetadataLabel("PERIOD", rider.protectionPeriod, COMMON_DICTS.protectionPeriod) || "-"} />
                  <InfoField label="供款年期" value={rider.contributionYears || "-"} />
                  <InfoField label="繳費頻率" value={getMetadataLabel(null, rider.paymentFrequency, COMMON_DICTS.paymentFrequency)} />
                </div>
              </div>
            ))}
          </div>
        </InfoCard>
      )}
    </div>
  );
}
